import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import {
    listAppUsers,
    listRecentMentions,
    userDisplay,
    type AppUser,
} from "../../services/userService";
import {
    activePersonToken,
    effectivePersonSelection,
    mentionToken,
    type PersonSelection,
} from "./mentions";
import { alleFenster } from "../windowManager/browserFenster/fensterRegister";

const MENTION_SUGGESTION_LIMIT = 50;

/**
 * Mitwachsendes Textfeld MIT @-Erwähnungs-Autocomplete (CR-SP3) — die
 * FORMULAR-Variante des NoteChat-Composers: statt sofort zu posten, reicht es
 * bei jeder Änderung `{ text, mentions }` in den Formular-State hoch
 * (deferred — die Vorgang-/Todo-Id existiert beim Erstellen noch nicht;
 * die Seite postet den Text nach dem Anlegen als erste Chat-Notiz).
 *
 * Userliste + Recent-Mentions-Ranking lädt die Komponente selbst (wie
 * NoteChat); das Vorschlags-Dropdown rendert per Portal mit position:fixed
 * (entkommt overflow-Containern, Muster UserMultiPicker).
 */
type Props = {
    value: string;
    onChange: (next: { text: string; mentions: string[]; zuInformieren: string[] }) => void;
    placeholder?: string;
    minRows?: number;
    className?: string;
    autoFocus?: boolean;
    /** Fokus verlassen — für Auto-Save-Formulare (Detail-Remake, onBlur = speichern). */
    onBlur?: () => void;
    /** `#Name` wählt zusätzlich eine zu informierende Person aus. */
    zuInformierenErlaubt?: boolean;
};

export const MentionTextarea: React.FC<Props> = ({
    value,
    onChange,
    placeholder = "Schreiben… mit @ jemanden erwähnen",
    minRows = 3,
    className,
    autoFocus,
    onBlur,
    zuInformierenErlaubt = false,
}) => {
    const [users, setUsers] = useState<AppUser[]>([]);
    const [recentMentionIds, setRecentMentionIds] = useState<string[]>([]);
    const [picked, setPicked] = useState<PersonSelection[]>([]);
    const [caret, setCaret] = useState(value.length);
    const taRef = useRef<HTMLTextAreaElement | null>(null);
    const dropRef = useRef<HTMLDivElement | null>(null);
    const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);

    useEffect(() => {
        listAppUsers().then(setUsers).catch(() => undefined);
        listRecentMentions().then(setRecentMentionIds).catch(() => undefined);
    }, []);

    useEffect(() => {
        if (autoFocus && taRef.current) {
            taRef.current.focus();
            const end = taRef.current.value.length;
            taRef.current.setSelectionRange(end, end);
        }
    }, [autoFocus]);

    // Auto-Grow (wie AutoGrowTextarea im Detail-Remake).
    useEffect(() => {
        const el = taRef.current;
        if (!el) return;
        el.style.height = "auto";
        el.style.height = `${el.scrollHeight}px`;
    }, [value]);

    const mention = activePersonToken(value, caret, zuInformierenErlaubt);

    const recentRank = useMemo(() => {
        const m = new Map<string, number>();
        recentMentionIds.forEach((id, i) => {
            if (!m.has(id)) m.set(id, i);
        });
        return m;
    }, [recentMentionIds]);

    const suggestions = useMemo(() => {
        if (!mention) return [];
        const q = mention.query.trim().toLowerCase();
        const matches = users.filter(
            (u) =>
                !q ||
                userDisplay(u).toLowerCase().includes(q) ||
                (u.email ?? "").toLowerCase().includes(q) ||
                (u.username ?? "").toLowerCase().includes(q),
        );
        const ranked = matches
            .map((u, idx) => ({
                u,
                idx,
                rank: recentRank.has(u.id) ? recentRank.get(u.id)! : Number.POSITIVE_INFINITY,
            }))
            .sort((a, b) => a.rank - b.rank || a.idx - b.idx);
        return ranked.map((r) => r.u).slice(0, MENTION_SUGGESTION_LIMIT);
    }, [mention, users, recentRank]);

    // Dropdown unter der Textarea positionieren (Portal, fixed).
    const open = !!mention && suggestions.length > 0;
    useEffect(() => {
        if (!open) return;
        const reposition = () => {
            const r = taRef.current?.getBoundingClientRect();
            if (r) setPos({ top: r.bottom + 4, left: r.left, width: r.width });
        };
        reposition();
        alleFenster.addEventListener("scroll", reposition, true);
        alleFenster.addEventListener("resize", reposition);
        return () => {
            alleFenster.removeEventListener("scroll", reposition, true);
            alleFenster.removeEventListener("resize", reposition);
        };
    }, [open]);

    const emit = (text: string, selections: PersonSelection[]) => {
        onChange({ text, ...effectivePersonSelection(text, selections, users) });
    };

    const pickMention = (u: AppUser) => {
        if (!mention) return;
        const before = value.slice(0, mention.start);
        const after = value.slice(caret);
        const inserted = `${mention.trigger}${mentionToken(u)} `;
        const next = before + inserted + after;
        const selection: PersonSelection = { userId: u.id, trigger: mention.trigger };
        const selections = picked.some((item) => item.userId === u.id && item.trigger === mention.trigger)
            ? picked
            : [...picked, selection];
        setPicked(selections);
        emit(next, selections);
        const newCaret = (before + inserted).length;
        requestAnimationFrame(() => {
            if (taRef.current) {
                taRef.current.focus();
                taRef.current.setSelectionRange(newCaret, newCaret);
                setCaret(newCaret);
            }
        });
    };

    return (
        <>
            <textarea
                ref={taRef}
                className={className}
                value={value}
                placeholder={placeholder}
                rows={minRows}
                onChange={(e) => {
                    setCaret(e.target.selectionStart ?? e.target.value.length);
                    emit(e.target.value, picked);
                }}
                onKeyUp={(e) => setCaret((e.target as HTMLTextAreaElement).selectionStart ?? 0)}
                onClick={(e) => setCaret((e.target as HTMLTextAreaElement).selectionStart ?? 0)}
                onBlur={() => onBlur?.()}
                style={
                    className
                        ? undefined
                        : {
                              width: "100%",
                              boxSizing: "border-box",
                              padding: "9px 11px",
                              border: "1px solid #ece7f0",
                              borderRadius: 10,
                              fontFamily: "inherit",
                              fontSize: 13,
                              resize: "none",
                              overflow: "hidden",
                              backgroundColor: "#fbf9fc",
                          }
                }
            />
            {open && pos &&
                createPortal(
                    <div
                        ref={dropRef}
                        style={{
                            position: "fixed",
                            top: pos.top,
                            left: pos.left,
                            width: pos.width,
                            zIndex: 100000,
                            backgroundColor: "#fff",
                            border: "1px solid #ece7f0",
                            borderRadius: 8,
                            boxShadow: "0 8px 24px rgba(20,8,20,0.12)",
                            maxHeight: 320,
                            overflowY: "auto",
                        }}
                    >
                        {suggestions.map((u) => (
                            <div
                                key={u.id}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    pickMention(u);
                                }}
                                style={{
                                    padding: "6px 12px",
                                    display: "flex",
                                    alignItems: "center",
                                    cursor: "pointer",
                                    fontSize: 13,
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#faf6fb")}
                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "")}
                            >
                                {userDisplay(u)}
                                {u.email && (
                                    <span style={{ color: "#6b5a66", marginLeft: 8, fontSize: 11 }}>{u.email}</span>
                                )}
                            </div>
                        ))}
                    </div>,
                    document.body,
                )}
        </>
    );
};
